import { MatPasswordStrengthComponent } from '@angular-material-extensions/password-strength';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, Login } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { Customer, CustomerLanding } from '@klickdata/core/customer';
import { FormHelper } from '@klickdata/core/form';
import { ErrorResponse } from '@klickdata/core/http/src/responce/error-responce';
import { MessageService } from '@klickdata/core/message/src/message.service';
import { Utils } from '@klickdata/core/util';
import { ActivationService } from '@klickdata/guest/src/activate/activation.service';
import { PasswordValidator } from 'apps/klickdata/src/app/shared/validator/password.validator';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-activate',
    templateUrl: './activate.component.html',
    styleUrls: ['./activate.component.scss'],
})
export class ActivateComponent implements OnInit, OnDestroy {
    @ViewChild(MatPasswordStrengthComponent) passwordComp: MatPasswordStrengthComponent;
    public activationGroup: FormGroup;
    academies: Customer[];
    public $error: Observable<ErrorResponse<null>>;
    private destroy = new Subject<boolean>();
    public customerLanding$: Observable<CustomerLanding>;

    constructor(
        protected fb: FormBuilder,
        protected activationService: ActivationService,
        protected message: MessageService,
        protected route: ActivatedRoute,
        protected configService: ConfigService,
        protected router: Router,
        auth: AuthService
    ) {
        this.customerLanding$ = auth
            .getCustomerLanding()
            .pipe(
                tap(() =>
                    setTimeout(() => this.activationGroup.setControl('password', this.passwordComp.passwordFormControl))
                )
            );
    }

    public ngOnInit() {
        this.buildForm();
        this.route.params
            .pipe(
                takeUntil(this.destroy),
                filter((params) => params.token),
                map((params) => params.token)
            )
            .subscribe((token) => this.activationGroup.patchValue({ token: token }));

        this.route.queryParams
            .pipe(
                takeUntil(this.destroy),
                filter((params) => params.username),
                map((params) => params.username)
            )
            .subscribe((username) => {
                const usernameCtrl = this.activationGroup.get('username');
                usernameCtrl.setValue(username);
                usernameCtrl.disable();
            });
    }

    public onSubmit(): void {
        if (this.activationGroup.invalid) {
            FormHelper.markForm(this.activationGroup);
            return;
        }
        this.activationService
            .activate(
                new Login({
                    ...this.activationGroup.value,
                    username: this.activationGroup.get('username').value,
                }).getData()
            )
            .subscribe(
                (res) => {
                    if (Array.isArray(res.data)) {
                        if (res.data.length) {
                            this.academies = res.data.map((cusData) => new Customer(cusData));
                        } else {
                            this.$error = of(res);
                        }
                    } else if (res) {
                        this.router.navigate(['/guest/login'], {
                            queryParams: {
                                username: this.activationGroup.get('username').value,
                            },
                        });
                    }
                },
                (err) => {
                    this.$error = of(err);
                }
            );
    }

    protected buildForm() {
        this.activationGroup = this.fb.group(
            {
                username: ['', Validators.required],
                password: ['', [Validators.required, Validators.minLength(6)]],
                password_confirmation: ['', [Validators.required, Validators.minLength(6)]],
                token: ['', Validators.required],
                short_name: [Utils.getSubdomain(), this.requiredWithAcademies()],
            },
            { validators: PasswordValidator.matchPassword }
        );
    }

    requiredWithAcademies(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return this.academies && !control.value ? { academyRequired: true } : null;
        };
    }

    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
